import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import { InternalBet } from '@cgs/data-access';
import { Lazy } from '@cgs/shared';
import {
  GAME_ACTION_HANDLER_TOKEN,
  GameActionHandler,
  LAUNCH_PARAMS_STORE_TOKEN,
  LaunchParamsStore,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  SoundInstance,
} from '@cgs/machines';
import {
  HUD_CONTROLLER_TOKEN,
  TrifectaBuyFeaturePopup,
  TrifectaHudController,
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaLaunchParamsDto,
} from '@cgs/provider-trifecta';
import { Button, Container, SceneObject, SoundSceneObject, TextSceneObject } from '@cgs/wge';

const enum SceneNames {
  BuyFeaturePopup = 'buyFeaturePopup',
  TotalBuy = 'count_total_buy',
  TotalBet = 'count_stake_buy',
  ButtonPlus = 'btn_plus',
  ButtonMinus = 'btn_minus',
  ButtonBuy = 'btn_buy',
  ButtonClose = 'btn_close',
  BackDropArea = 'buyFeatureAreaBtn',
  ClickSound = 'button_click',
}

const enum States {
  Show = 'show',
  Hide = 'hide',
  Empty = 'empty',
}

export class CorsicaBuyFeaturePopup extends TrifectaBuyFeaturePopup {
  private readonly popup: SceneObject;
  private readonly numberFormatter: INumberFormatter;
  private readonly launchParamsStore: LaunchParamsStore;
  private readonly TrifectaLaunchParamsDto: TrifectaLaunchParamsDto;
  private readonly hudController: Lazy<TrifectaHudController>;

  private readonly totalBuy: TextSceneObject;
  private readonly totalBet: TextSceneObject;

  private currentBetIndex: number;
  private readonly betValues: InternalBet[];

  private readonly currencyDecimals: number;

  private readonly buyFeatureMultiplier: number;

  private readonly soundClick: SoundInstance;

  private readonly gameActionHandler: GameActionHandler;

  constructor(container: Container) {
    super(container);

    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    const popup = this.scene.findById<SceneObject>(SceneNames.BuyFeaturePopup);

    if (!popup) {
      throw new Error('Popup not found');
    }

    this.gameActionHandler = container.resolve<GameActionHandler>(GAME_ACTION_HANDLER_TOKEN);
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
    this.hudController = new Lazy(() =>
      container.resolve<TrifectaHudController>(HUD_CONTROLLER_TOKEN)
    );

    const resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    const soundClickScene = resourcesComponent.sounds
      .findById<SoundSceneObject>(SceneNames.ClickSound)
      ?.findAllByType(SoundSceneObject)[0];

    this.soundClick = new SoundInstance(soundClickScene || null);

    this.currencyDecimals = launchParams.currencyDecimals;

    this.buyFeatureMultiplier = launchParams.buyFeatureMultiplier;

    this.popup = popup;

    popup.initialize();

    const totalBuy = popup.findById<TextSceneObject>(SceneNames.TotalBuy);
    const totalBet = popup.findById<TextSceneObject>(SceneNames.TotalBet);
    const buttonPlus = popup.findById<Button>(SceneNames.ButtonPlus);
    const buttonMinus = popup.findById<Button>(SceneNames.ButtonMinus);
    const buttonBuy = popup.findById<Button>(SceneNames.ButtonBuy);
    const buttonClose = popup.findById<Button>(SceneNames.ButtonClose);
    const backdropArea = popup.findById<Button>(SceneNames.BackDropArea);

    if (!backdropArea) {
      throw new Error('Backdrop area not found');
    }

    if (!buttonClose) {
      throw new Error('Button close not found');
    }

    if (!totalBuy) {
      throw new Error('Total buy not found');
    }

    if (!totalBet) {
      throw new Error('Total bet not found');
    }

    if (!buttonPlus) {
      throw new Error('Button plus not found');
    }

    if (!buttonMinus) {
      throw new Error('Button minus not found');
    }

    if (!buttonBuy) {
      throw new Error('Button buy not found');
    }

    if (!backdropArea) {
      throw new Error('Backdrop area not found');
    }

    this.totalBuy = totalBuy;
    this.totalBet = totalBet;
    this.launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    this.TrifectaLaunchParamsDto = container.resolve<TrifectaLaunchParamsDto>(
      TRIFECTA_LAUNCH_PARAMS_TOKEN
    );

    this.betValues = this.launchParamsStore.state.bets;

    buttonPlus.clicked.listen(() => {
      this.playClickSound();
      this.handleBetChanged(1);
    });

    buttonMinus.clicked.listen(() => {
      this.playClickSound();
      this.handleBetChanged(-1);
    });

    buttonBuy.clicked.listen(() => {
      this.playClickSound();
      this.handleBuyFeature();
    });

    buttonClose.clicked.listen(() => {
      this.playClickSound();
      this.hide();
    });

    backdropArea.clicked.listen(() => {
      this.hide();
    });
  }

  public show(): void {
    this.renderBet(this.hudController.call().getCurrentBet());

    this.popup.stateMachine?.switchToState(States.Show);
    this.isShown.emit(true);
  }

  public hide(): void {
    this.popup.stateMachine?.switchToState(States.Hide);
    this.popup.stateMachine?.switchToState(States.Empty);
    this.currentBetIndex = -1;
    this.isShown.emit(false);
  }

  private renderBet(startBet: number): void {
    this.currentBetIndex = this.getCurrentBetIndex();
    const totalBet = startBet * this.launchParamsStore.state.maxLines;
    const totalBuy = totalBet * this.buyFeatureMultiplier;

    this.totalBuy.text = this.numberFormatter.formatMoney(totalBuy, this.currencyDecimals);
    this.totalBet.text = this.numberFormatter.formatMoney(totalBet, this.currencyDecimals);
  }

  private handleBetChanged(direction: -1 | 1): void {
    if (!this.betValues[this.currentBetIndex + direction]) {
      return;
    }

    const newBet = this.betValues[this.currentBetIndex + direction].bet;
    this.currentBetIndex += direction;
    this.renderBet(newBet);
  }

  private handleBuyFeature(): void {
    const bet = this.launchParamsStore.state.bets[this.currentBetIndex].bet;
    const featurePrice =
      bet *
      this.launchParamsStore.state.maxLines *
      this.TrifectaLaunchParamsDto.buyFeatureMultiplier;

    this.gameActionHandler.buyFeature({ bet, featurePrice });

    this.hide();
  }

  private getCurrentBetIndex(): number {
    if (this.currentBetIndex !== undefined && this.currentBetIndex !== -1) {
      return this.currentBetIndex;
    }

    const currentBet = this.hudController.call().getCurrentBet();
    return this.betValues.findIndex((bet) => bet.bet === currentBet);
  }

  private playClickSound(): void {
    this.soundClick.stop();
    this.soundClick.play();
  }
}
